import { useState, useCallback } from 'react';
import Cookies from 'universal-cookie';
import { setCrearSesionPlaceToPayLibranzas } from '@/services/setCrearSesionPlaceToPayLibranzas';
import { setCancelarSesionPlaceToPayLibranzas } from '@/services/setCancelarSesionPlaceToPayLibranzas';
import { SITE_URL } from '@/constants/api-constants';

export interface BuyerInfo {
    document: string;
    documentType: string;
    documentCompany: string;
    documentTypeCompany: string;
    email: string;
    mobile: string;
    name: string;
    surname: string;
}

export interface PaymentItem {
    reference: string;
    total: number;
}

/** Única fuente de verdad para si la modal de confirmación de pago (éxito o pago repetido) está abierta. */
type ModalPagoEstado = 'none' | 'confirmacion';

export interface PayBlockedInfo {
    nit: string;
}

export interface UseLibranzasPagoResult {
    isPaying: boolean;
    payBlocked: boolean;
    payMessage: string | null;
    setPayMessage: (msg: string | null) => void;
    payBlockedInfo: PayBlockedInfo | null;
    setPayBlockedInfo: (info: PayBlockedInfo | null) => void;
    setPayBlocked: (blocked: boolean) => void;
    urlToPay: string;
    showConfirmacionPago: boolean;
    setShowConfirmacionPago: (show: boolean) => void;
    /** true cuando la sesión se creó con errorCode==2 (pago repetido a la misma referencia en <24h). */
    isPagoRepetido: boolean;
    payingCompanyNit: string;
    handleCancelConfirmacionPago: () => void;
    pagar: (buyer: BuyerInfo, payments: PaymentItem[], requestId?: string) => void;
}

export function useLibranzasPago(): UseLibranzasPagoResult {
    const [isPaying, setIsPaying] = useState(false);
    const [payBlocked, setPayBlocked] = useState(false);
    const [payMessage, setPayMessage] = useState<string | null>(null);
    const [payBlockedInfo, setPayBlockedInfo] = useState<PayBlockedInfo | null>(null);
    const [urlToPay, setUrlToPay] = useState('');
    const [modalPago, setModalPago] = useState<ModalPagoEstado>('none');
    const [isPagoRepetido, setIsPagoRepetido] = useState(false);
    const [payingCompanyNit, setPayingCompanyNit] = useState('');

    const showConfirmacionPago = modalPago === 'confirmacion';
    const setShowConfirmacionPago = useCallback(
        (show: boolean) => setModalPago(show ? 'confirmacion' : 'none'),
        []
    );

    const saveCookie = (reference: string, rid: string, payments: PaymentItem[], expiresIn?: Date) => {
        const data = btoa(
            JSON.stringify({
                reference,
                requestId: rid,
                references: payments.map((p) => ({ code: p.reference, description: p.reference })),
            })
        );
        new Cookies().set('transactionData', data, {
            path: '/',
            ...(expiresIn ? { expires: expiresIn } : {}),
        });
    };

    // Unifica la lógica de cancelación — bug fix: try/catch alrededor de atob+JSON.parse
    const cancelSesion = useCallback((onDone: () => void) => {
        const cookie = new Cookies().get('transactionData');
        if (!cookie) {
            onDone();
            return;
        }
        try {
            const { requestId: rid } = JSON.parse(atob(cookie));
            setCancelarSesionPlaceToPayLibranzas(rid).finally(() => {
                onDone();
                window.location.href = `${SITE_URL}/portal-servicios/pagos`;
            });
        } catch {
            onDone();
        }
    }, []);

    const handleCancelConfirmacionPago = useCallback(
        () => cancelSesion(() => setShowConfirmacionPago(false)),
        [cancelSesion, setShowConfirmacionPago]
    );

const pagar = useCallback((buyer: BuyerInfo, payments: PaymentItem[], requestId?: string) => {
        if (payments.length === 0) return;

        setIsPaying(true);
        setPayMessage(null);
        setPayBlockedInfo(null);
        setModalPago('none');
        setIsPagoRepetido(false);
        setPayingCompanyNit(buyer.documentCompany);
        setCrearSesionPlaceToPayLibranzas({
            buyer,
            payments,
            returnUrl: `${SITE_URL}/portal-servicios/pagos/confirmacion-estado-pago`,
            cancelUrl: `${SITE_URL}/portal-servicios/pagos/confirmacion-estado-pago`,
            ...(requestId ? { requestId } : {}),
        })
            .then((response) => {
                const wrapper = response?.data?.data;
                const payload = wrapper?.data;
                const processUrl: string =
                    payload?.request?.fields?.[0]?.value ?? '';
                const reference: string = payload?.reference ?? '';
                const rid: string = payload?.requestId ?? '';
                const userMessage: string = wrapper?.userMessage ?? '';
                const developerMessage: string =
                    wrapper?.developerMessage ?? '';
                const errorCode = wrapper?.errorCode;

                if (developerMessage)
                    console.error('[crear-sesion]', developerMessage);

                if (errorCode == 1) {
                    setPayBlocked(true);
                    setPayBlockedInfo({
                        nit: payload?.buyer?.documentCompany || buyer.documentCompany,
                    });
                } else if (errorCode == 2) {
                    saveCookie(
                        reference,
                        rid,
                        payments,
                        new Date(Date.now() + 15 * 60 * 1000)
                    );
                    setUrlToPay(processUrl);
                    setIsPagoRepetido(true);
                    setShowConfirmacionPago(true);
                } else if (processUrl) {
                    saveCookie(reference, rid, payments);
                    setUrlToPay(processUrl);
                    setIsPagoRepetido(false);
                    setShowConfirmacionPago(true);
                } else {
                    setPayMessage(
                        userMessage ||
                            'No se pudo iniciar el pago. Por favor intente de nuevo.'
                    );
                }
            })
            .catch((error) => {
                console.error('[pagar]', error);
                setPayMessage(
                    'No se pudo iniciar el pago. Por favor intente de nuevo.'
                );
            })
            .finally(() => setIsPaying(false));
    }, []);

    return {
        isPaying,
        payBlocked,
        payMessage,
        setPayMessage,
        payBlockedInfo,
        setPayBlockedInfo,
        setPayBlocked,
        urlToPay,
        showConfirmacionPago,
        setShowConfirmacionPago,
        isPagoRepetido,
        payingCompanyNit,
        handleCancelConfirmacionPago,
        pagar,
    };
}
